// lib/sand-v3/shaders/sandSurface.ts
//
// Screen-space heightfield sand shader. height(uv) = raked grooves + rings
// around stones + footprint-texture dents; normal from the height gradient;
// Lambert + optional specular with the sand albedo ramp. Stones render as
// domed SDF forms with rim/underside light and a contact-shadow ring.
//
// GLSL is authored as a template string; do NOT use backticks inside it and do
// NOT name identifiers sample/filter/patch/input/output (reserved-word traps).

import { ShaderMaterial, Vector2, Vector3, Vector4, GLSL3 } from 'three';
import { sandVec3 } from '@/lib/sand-v3/palette';
import { MAX_STONES } from '@/lib/sand-v3/interaction/stoneRects';

export interface SandSurfaceOpts { rings: boolean; specular: boolean; grain: boolean }

export const SAND_VERTEX = `
out vec2 vUv;
void main() {
  vUv = vec2(uv.x, 1.0 - uv.y);
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

// NOTE: vUv origin is top-left with v increasing downward to match DOM/stone
// coordinates. The vertex shader flips Three.js PlaneGeometry's v-UP UVs to
// v-DOWN so vUv aligns with DepthMap (row 0 = screen top) and stone rects
// (centerV computed from DOM top). Raw uv from PlaneGeometry is NOT used.
export const SAND_FRAGMENT = `
precision highp float;
in vec2 vUv;
out vec4 fragColor;

uniform vec2  uResolution;
uniform float uTime;
uniform sampler2D uFootprint;
uniform vec2  uFootprintTexel;   // 1.0 / footprintTextureSize
uniform vec4  uStones[${MAX_STONES}];
uniform int   uStoneCount;
uniform vec3  uLightDir;         // normalized, points toward the light
uniform float uRings;            // 0/1 feature flag
uniform float uSpecular;         // 0/1 feature flag
uniform float uGrain;            // 0/1 feature flag

const vec3 CREST   = ${sandVec3('crest')};
const vec3 MID     = ${sandVec3('mid')};
const vec3 MID2    = ${sandVec3('mid2')};
const vec3 TROUGH  = ${sandVec3('trough')};
const vec3 KEY     = ${sandVec3('keyLight')};
const vec3 AMBIENT = ${sandVec3('ambient')};
const vec3 STONE_BODY  = ${sandVec3('stoneBody')};
const vec3 STONE_KEY   = ${sandVec3('stoneKey')};
const vec3 STONE_RIM   = ${sandVec3('stoneRim')};
const vec3 STONE_UNDER = ${sandVec3('stoneUnder')};
const vec3 AO_TINT     = ${sandVec3('aoTint')};

const float PI = 3.14159265;
const float RAKE_ANGLE = 0.35;   // radians
const float GROOVE_FREQ = 210.0; // ~20px pitch at 1080p open field
const float RING_FREQ   = 260.0; // ~16px pitch rings

// cheap hash + value noise for dune modulation and grain
float hash(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float vnoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i), b = hash(i + vec2(1.0, 0.0)), c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// layered value noise (fractal Brownian motion) for organic warping + mottle
float fbm(vec2 p) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { s += a * vnoise(p); p *= 2.03; a *= 0.5; }
  return s;
}

// distance from uv to the nearest stone edge (negative inside), plus index
float stoneField(vec2 uv, out int nearest) {
  float best = 1e9; nearest = -1;
  for (int i = 0; i < ${MAX_STONES}; i++) {
    if (i >= uStoneCount) break;
    vec4 s = uStones[i];
    vec2 d = (uv - s.xy) / max(s.zw, vec2(1e-4));
    float e = (length(d) - 1.0) * min(s.z, s.w); // approx signed dist in uv units
    if (e < best) { best = e; nearest = i; }
  }
  return best;
}

// procedural sand height (grooves + rings), excluding footprints
float baseHeight(vec2 uv) {
  // Hand-raked grooves. Domain-warping the rake axis with low-freq fbm makes the
  // lines meander like real hand-raking instead of machine-uniform corrugation
  // (the "cheese ripple" failure mode). Per-region amplitude varies the depth.
  vec2 dir = vec2(cos(RAKE_ANGLE), sin(RAKE_ANGLE));
  float warp = (fbm(uv * 2.0) - 0.5) * 0.05;
  float amp  = 0.13 + 0.05 * vnoise(uv * 1.7 + 11.0);
  float g = sin((dot(uv, dir) + warp) * GROOVE_FREQ);
  g = sign(g) * pow(abs(g), 0.8); // V-troughed grooves, rounded crests
  float hgt = g * amp;
  // concentric rings around the nearest stone, fading with distance
  if (uRings > 0.5) {
    int ni;
    float sd = stoneField(uv, ni);
    if (ni >= 0) {
      float rings = sin(sd * RING_FREQ);
      float fade = smoothstep(0.26, 0.02, sd); // strong near the stone
      hgt = mix(hgt, rings * 0.30, clamp(fade, 0.0, 1.0));
    }
  }
  // gentle large-scale undulation (reduced) + grain-scale micro-roughness so the
  // normal picks up individual-grain sparkle instead of reading as a smooth wax
  hgt += (fbm(uv * 3.5) - 0.5) * 0.18;
  hgt += (vnoise(uv * uResolution * 0.28) - 0.5) * 0.05;
  return hgt;
}

float footprintHeight(vec2 uv) {
  // stored as depth (positive = pressed down); negate so a dent lowers height
  return -texture(uFootprint, uv).r;
}

float heightAt(vec2 uv) {
  return baseHeight(uv) + footprintHeight(uv) * 1.6;
}

void main() {
  vec2 uv = vUv;

  // normal from height gradient (finite differences in screen texels)
  vec2 e = 1.0 / uResolution;
  float hL = heightAt(uv - vec2(e.x, 0.0));
  float hR = heightAt(uv + vec2(e.x, 0.0));
  float hD = heightAt(uv - vec2(0.0, e.y));
  float hU = heightAt(uv + vec2(0.0, e.y));
  // scale the slope so lighting reads without exploding (softened for a
  // refined raked-sand look rather than harsh corrugation)
  vec3 n = normalize(vec3((hL - hR) * 1.9, (hD - hU) * 1.9, 1.0));

  vec3 L = normalize(uLightDir);
  float diff = clamp(dot(n, L), 0.0, 1.0);

  // multi-tone base: noise-mix the two mid tones so the field is never one flat
  // hue (the "cheese" read), then push toward crest on ridges and trough in
  // grooves. Ranges match the actual height scale (~±0.25).
  float h = heightAt(uv);
  float tone = fbm(uv * 4.0);
  vec3 albedo = mix(MID, MID2, tone);
  albedo = mix(albedo, CREST, smoothstep(0.02, 0.35, h) * 0.55);
  albedo = mix(albedo, TROUGH, smoothstep(-0.02, -0.35, h) * 0.5);
  // fine grain mottle breaks up remaining smoothness into granular texture
  float mottle = vnoise(uv * uResolution * 0.22);
  albedo *= 0.93 + 0.13 * mottle;

  // near-neutral lighting so the warm sand reads as tan, not saturated gold
  vec3 col = albedo * (AMBIENT * 0.80 + KEY * diff * 0.82);

  if (uSpecular > 0.5) {
    vec3 V = vec3(0.0, 0.0, 1.0);
    vec3 H = normalize(L + V);
    float spec = pow(clamp(dot(n, H), 0.0, 1.0), 48.0);
    col += KEY * spec * 0.18 * smoothstep(0.2, 0.6, h); // crests only
  }

  // stones on top
  int ni;
  float sd = stoneField(uv, ni);
  if (ni >= 0) {
    // contact-shadow AO — a soft warm gradient just outside the stone (wide
    // falloff, gentle strength) so the stone sits in the sand rather than
    // punching a hard dark ring around a void
    float ao = smoothstep(0.10, 0.0, sd) * step(0.0, sd);
    col = mix(col, col * mix(vec3(1.0), AO_TINT / max(max(AO_TINT.r, AO_TINT.g), AO_TINT.b), 0.5), ao * 0.32);

    if (sd < 0.0) {
      vec4 s = uStones[ni];
      vec2 d = (uv - s.xy) / max(s.zw, vec2(1e-4)); // -1..1 inside
      float r2 = clamp(dot(d, d), 0.0, 1.0);
      // domed normal: z from height of a hemisphere
      vec3 sn = normalize(vec3(d, sqrt(max(1.0 - r2, 0.0)) + 0.15));
      float sdiff = clamp(dot(sn, L), 0.0, 1.0);
      float rim = pow(1.0 - clamp(sn.z, 0.0, 1.0), 2.0);
      float under = clamp(-sn.y, 0.0, 1.0);
      vec3 stone = STONE_BODY * (0.48 + 0.62 * sdiff);
      stone = mix(stone, STONE_KEY, sdiff * 0.5);
      stone = mix(stone, STONE_RIM, rim * 0.4);
      stone = mix(stone, STONE_UNDER, under * 0.25);
      // subtle granite mottle
      stone *= 0.92 + 0.08 * vnoise(uv * uResolution * 0.25);
      float edge = smoothstep(0.0, -0.01, sd); // antialias the rim
      col = mix(col, stone, edge);
    }
  }

  if (uGrain > 0.5) {
    // STATIC speckle (no uTime) — animated per-pixel noise reads as TV shimmer,
    // not sand grain. Higher amplitude gives visible granular tooth.
    float g = hash(uv * uResolution) - 0.5;
    col += g * 0.03;
  }

  // gentle vignette (light — heavy corners add to the waxy/heavy read)
  vec2 q = uv - 0.5;
  col *= 1.0 - dot(q, q) * 0.22;

  fragColor = vec4(col, 1.0);
}
`;

export function makeSandSurfaceMaterial(opts: SandSurfaceOpts): ShaderMaterial {
  const stones: Vector4[] = Array.from({ length: MAX_STONES }, () => new Vector4(0, 0, 0, 0));
  return new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: SAND_VERTEX,
    fragmentShader: SAND_FRAGMENT,
    uniforms: {
      uResolution: { value: new Vector2(1, 1) },
      uTime: { value: 0 },
      uFootprint: { value: null },
      uFootprintTexel: { value: new Vector2(1, 1) },
      uStones: { value: stones },
      uStoneCount: { value: 0 },
      uLightDir: { value: new Vector3(-0.7, -0.7, 0.6).normalize() },
      uRings: { value: opts.rings ? 1 : 0 },
      uSpecular: { value: opts.specular ? 1 : 0 },
      uGrain: { value: opts.grain ? 1 : 0 },
    },
  });
}
